import React, { useEffect, useState } from "react";
import { X, Send, Loader2 } from "lucide-react";
import { WHATSAPP_URL } from "../lib/constants";

/**
 * Trinca3em1QuoteModal
 * Formulário de cotação para a série "Internacional 3 em 1".
 * Segue o padrão visual do SchoolQuoteModal (mesmas classes sq-*) e envia
 * o resumo estruturado para o WhatsApp da Orion.
 *
 * Props:
 *   onClose            (fn)     fecha o modal
 *   trincas            (array)  lista de trincas exibidas na página, para preencher o select
 *   defaultTrincaSlug  (string) slug pré-selecionado quando aberto por um card específico
 */
const PERFIL_OPTIONS = [
  "Casal",
  "Família com filhos",
  "Amigos / Grupo",
  "Lua de mel",
  "Solo",
  "Aniversário / Comemoração especial",
];

const DURACAO_OPTIONS = [
  "7 a 9 dias",
  "10 a 12 dias",
  "13 a 15 dias",
  "16 a 20 dias",
  "Mais de 20 dias",
  "Ainda em definição",
];

const BUDGET_OPTIONS = [
  "Até R$ 15.000 por pessoa",
  "R$ 15.000 a R$ 25.000 por pessoa",
  "R$ 25.000 a R$ 40.000 por pessoa",
  "Acima de R$ 40.000 por pessoa",
  "Prefiro não informar",
];

const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
  "Ainda em definição",
];

export default function Trinca3em1QuoteModal({ onClose, trincas = [], defaultTrincaSlug = "" }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [trincaSlug, setTrincaSlug] = useState(defaultTrincaSlug || (trincas[0]?.slug ?? ""));
  const [travelers, setTravelers] = useState(2);
  const [profile, setProfile] = useState(PERFIL_OPTIONS[0]);
  const [mes, setMes] = useState("");
  const [duracao, setDuracao] = useState(DURACAO_OPTIONS[1]);
  const [budget, setBudget] = useState(BUDGET_OPTIONS[1]);
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const canSend = name.trim().length >= 2 && phone.trim().length >= 8 && travelers > 0 && trincaSlug;

  const submit = () => {
    if (!canSend) return;
    setSending(true);
    const trinca = trincas.find((t) => t.slug === trincaSlug);
    const trincaLabel = trinca ? `${trinca.cities.join(" + ")} (${trinca.region})` : trincaSlug;
    const lines = [
      "Olá, Orion!",
      "",
      "*Solicitação de Cotação · Internacional 3 em 1*",
      "",
      `*Contato:* ${name}`,
      `*WhatsApp:* ${phone}`,
      city ? `*Cidade / Estado:* ${city}` : "",
      "",
      `*Trinca desejada:* ${trincaLabel}`,
      `*Perfil da viagem:* ${profile}`,
      `*Nº de viajantes:* ${travelers}`,
      `*Duração desejada:* ${duracao}`,
      `*Mês previsto:* ${mes || "A definir"}`,
      `*Orçamento por pessoa:* ${budget}`,
      notes ? `*Observações:* ${notes}` : "",
      "",
      "Podem preparar a proposta 3 em 1 com voo, hospedagem, deslocamento interno e roteiro dia-a-dia?",
      "(Todas as trincas Orion incluem Cadastur ativo, contrato formal, assistência 24h em português e parcelamento em até 12× sem juros.)",
    ];
    const text = lines.filter(Boolean).join("\n");

    let phoneWA = "5511920043024";
    try {
      const m = WHATSAPP_URL.match(/wa\.me\/(\d+)/);
      if (m) phoneWA = m[1];
    } catch { /* fall through */ }
    const url = `https://wa.me/${phoneWA}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener");
    setSending(false);
    onClose();
  };

  return (
    <div className="sq-overlay" onClick={onClose} data-testid="int3-quote-modal">
      <div className="sq-modal" onClick={(e) => e.stopPropagation()}>
        <button className="sq-close" onClick={onClose} aria-label="Fechar" data-testid="int3-quote-close">
          <X size={18} />
        </button>
        <header className="sq-head">
          <div className="sq-eyebrow">◈ Cotação · Internacional 3 em 1</div>
          <h2 className="sq-title">Consultar Roteiro · 3 em 1</h2>
          <p className="sq-sub">
            Preencha em 60 segundos — a Orion devolve com voo + hospedagem + deslocamento interno + roteiro dia-a-dia da sua trinca. Parcelamos em 12× sem juros.
          </p>
        </header>

        <div className="sq-body">
          <div className="sq-grid">
            <label className="sq-field">
              <span>Nome completo *</span>
              <input value={name} onChange={(e) => setName(e.target.value)} type="text" placeholder="Ex.: Ana Silva" data-testid="int3q-name" />
            </label>
            <label className="sq-field">
              <span>WhatsApp *</span>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" placeholder="(11) 91234-5678" data-testid="int3q-phone" />
            </label>
            <label className="sq-field sq-col-2">
              <span>Cidade e estado onde mora</span>
              <input value={city} onChange={(e) => setCity(e.target.value)} type="text" placeholder="Ex.: São Paulo — SP" data-testid="int3q-city" />
            </label>
            <label className="sq-field sq-col-2">
              <span>Trinca desejada *</span>
              <select value={trincaSlug} onChange={(e) => setTrincaSlug(e.target.value)} data-testid="int3q-trinca">
                {trincas.map((t) => (
                  <option key={t.slug} value={t.slug}>
                    {String(t.i).padStart(2, "0")} · {t.cities.join(" + ")}  ({t.region})
                  </option>
                ))}
                <option value="__outra__">Outra combinação (montar sob medida)</option>
              </select>
            </label>
            <label className="sq-field">
              <span>Perfil da viagem *</span>
              <select value={profile} onChange={(e) => setProfile(e.target.value)} data-testid="int3q-profile">
                {PERFIL_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
            <label className="sq-field">
              <span>Nº de viajantes *</span>
              <input
                value={travelers}
                onChange={(e) => setTravelers(Math.max(1, Number(e.target.value) || 1))}
                type="number"
                min="1"
                data-testid="int3q-travelers"
              />
            </label>
            <label className="sq-field">
              <span>Duração desejada *</span>
              <select value={duracao} onChange={(e) => setDuracao(e.target.value)} data-testid="int3q-duracao">
                {DURACAO_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
            <label className="sq-field">
              <span>Mês previsto de embarque</span>
              <select value={mes} onChange={(e) => setMes(e.target.value)} data-testid="int3q-month">
                <option value="">Escolher mês…</option>
                {MESES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label className="sq-field sq-col-2">
              <span>Orçamento por pessoa</span>
              <select value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="int3q-budget">
                {BUDGET_OPTIONS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </label>
            <label className="sq-field sq-col-2">
              <span>Observações & pedidos especiais</span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Ex.: Restrição alimentar, mobilidade reduzida, quero adicionar 1 cidade extra, prefiro voos diurnos, quero pacote com jantar-degustação…"
                data-testid="int3q-notes"
              />
            </label>
          </div>

          <button className="sq-submit" disabled={!canSend || sending} onClick={submit} data-testid="int3q-submit">
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            Enviar cotação via WhatsApp
          </button>
          <p className="sq-fine">
            Ao enviar, o pedido é aberto no WhatsApp da Orion com todos os dados já preenchidos —
            responderemos com a proposta completa em minutos.
          </p>
        </div>
      </div>

      <style>{`
        .sq-overlay {
          position: fixed; inset: 0; z-index: 100;
          background: rgba(4, 1, 15, 0.7);
          backdrop-filter: blur(6px);
          display: flex; align-items: center; justify-content: center;
          padding: 20px;
          animation: sq-fade 200ms ease;
        }
        .sq-modal {
          position: relative;
          width: min(880px, 100%);
          max-height: 92vh;
          overflow-y: auto;
          background: #FFFFFF;
          border-radius: 14px;
          box-shadow: 0 40px 90px -20px rgba(44, 17, 57, 0.55);
          animation: sq-slide 240ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .sq-close {
          position: absolute; top: 14px; right: 14px;
          width: 36px; height: 36px;
          border-radius: 50%;
          background: rgba(44, 17, 57, 0.06);
          color: #2C1139;
          display: flex; align-items: center; justify-content: center;
          transition: background 160ms ease;
          z-index: 5;
        }
        .sq-close:hover { background: rgba(44, 17, 57, 0.14); }
        .sq-head {
          padding: 32px 32px 20px;
          border-bottom: 1px solid #EFEAE0;
          background:
            linear-gradient(180deg, rgba(197, 160, 89, 0.08) 0%, rgba(255, 255, 255, 0) 100%);
        }
        .sq-eyebrow {
          font-size: 11px;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: #C5A059;
          font-weight: 700;
          margin-bottom: 6px;
        }
        .sq-title {
          font-family: 'Playfair Display', serif;
          font-size: 26px;
          color: #2C1139;
          line-height: 1.2;
          margin: 0 0 10px;
        }
        .sq-sub {
          font-size: 13px;
          color: #594A63;
          line-height: 1.55;
          margin: 0;
        }
        .sq-body { padding: 22px 32px 32px; }
        .sq-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px 18px;
        }
        .sq-field {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .sq-field.sq-col-2 { grid-column: span 2; }
        .sq-field span {
          font-size: 11px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #594A63;
          font-weight: 600;
        }
        .sq-field input,
        .sq-field select,
        .sq-field textarea {
          width: 100%;
          padding: 11px 13px;
          font-size: 14px;
          color: #2C1139;
          background: #FDFBF7;
          border: 1px solid #EFEAE0;
          border-radius: 8px;
          transition: border-color 160ms ease, box-shadow 160ms ease;
          font-family: inherit;
        }
        .sq-field textarea { resize: vertical; }
        .sq-field input:focus,
        .sq-field select:focus,
        .sq-field textarea:focus {
          outline: none;
          border-color: #FF5A36;
          box-shadow: 0 0 0 3px rgba(255, 90, 54, 0.15);
        }
        .sq-submit {
          margin-top: 22px;
          width: 100%;
          background: #2C1139;
          color: #FFFFFF;
          padding: 14px 24px;
          border-radius: 999px;
          font-size: 12px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: transform 160ms ease, background 160ms ease;
        }
        .sq-submit:hover:not(:disabled) {
          background: #FF5A36;
          transform: translateY(-1px);
        }
        .sq-submit:disabled { opacity: 0.5; cursor: not-allowed; }
        .sq-fine {
          margin-top: 10px;
          text-align: center;
          font-size: 11px;
          color: #8A8191;
          line-height: 1.5;
        }
        @keyframes sq-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes sq-slide { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @media (max-width: 640px) {
          .sq-head { padding: 24px 22px 18px; }
          .sq-body { padding: 18px 22px 26px; }
          .sq-grid { grid-template-columns: 1fr; }
          .sq-field.sq-col-2 { grid-column: span 1; }
          .sq-title { font-size: 22px; }
        }
      `}</style>
    </div>
  );
}
